import React, { useEffect, useRef, useState } from 'react'
import {
  Search as SearchIcon,
  AlertTriangle,
  Sparkles,
  MessageSquare,
  History as HistoryIcon,
  Trash2,
  Zap,
  RotateCw,
  Languages,
  Loader2,
} from 'lucide-react'
import { useQueryClient } from '@tanstack/react-query'
import Button from '../components/ui/Button'
import Markdown from '../components/Markdown'
import DocumentViewer from '../components/DocumentViewer'
import ListenButton from '../components/ListenButton'
import { TARGET_LANGS } from '../components/TranslateButton'
import { langLabel } from '../components/ProjectBar'
import { getDocument } from '../services/ragApi'
import { stripChunkHeader, sourceLink } from '../components/docFormat'
import { canEmbed } from '../components/VideoPlayer'
import usePlayerStore from '../stores/playerStore'

// ▶ HH:MM:SS chip on a reference/result. Click opens the EMBEDDED player at
// that second (YouTube/Rutube); platforms without a seekable embed fall back
// to the external link; no provenance → plain marker.
function TimeChip({ metadata }) {
  const openPlayer = usePlayerStore((s) => s.openPlayer)
  const t = metadata?.start_time_formatted
  if (!t) return null
  const cls = 'font-mono text-[11px] font-normal text-gold-deep dark:text-gold/80'
  if (canEmbed(metadata)) {
    return (
      <button
        onClick={(e) => {
          e.stopPropagation()
          openPlayer({
            source_url: metadata.source_url,
            origin: metadata.origin,
            video_id: metadata.video_id,
            title: metadata.title,
            start: metadata.start_time,
          })
        }}
        title="Watch the source at this moment"
        className={`${cls} hover:underline`}
      >
        ▶ {t}
      </button>
    )
  }
  const href = sourceLink(metadata, metadata.start_time)
  return href ? (
    <a
      href={href}
      target="_blank"
      rel="noreferrer"
      onClick={(e) => e.stopPropagation()}
      title="Open the video at this moment"
      className={`${cls} hover:underline`}
    >
      ▶ {t}
    </a>
  ) : (
    <span className={cls}>▶ {t}</span>
  )
}
import {
  useProjects,
  useSearch,
  useAsk,
  useAskScope,
  useStrategies,
  useHistory,
  useDeleteHistory,
  useClearHistory,
} from '../hooks/useRag'
import { useTranslateAnswer, useTruthLens, useCodeRepos } from '../hooks/useResearch'
import TruthLensPanel, { TruthLensButton, ClaimVerifier } from '../components/TruthLens'
import { useDefaults } from '../hooks/useSettings'

// 2-letter language → a TTS locale for the Listen button.
const TTS_LOCALE = {
  en: 'en-US', cs: 'cs-CZ', de: 'de-DE', fr: 'fr-FR', es: 'es-ES', it: 'it-IT',
  pl: 'pl-PL', pt: 'pt-PT', ru: 'ru-RU', uk: 'uk-UA', nl: 'nl-NL',
}
const ttsLocale = (l) => TTS_LOCALE[l] || (l ? `${l}-${String(l).toUpperCase()}` : 'en-US')

const PROVIDERS_NOTE = '⚠ Generates an answer with a cloud LLM — costs apply.'

export default function SearchPage({ seedQuestion = '', seedNonce = 0, embedded = false }) {
  const qc = useQueryClient()
  const projectsQuery = useProjects()
  const search = useSearch()
  const ask = useAsk()
  const strategiesQuery = useStrategies()

  const [mode, setMode] = useState('search') // 'search' | 'ask'
  const [project, setProject] = useState('')
  const [query, setQuery] = useState('')
  const [strategy, setStrategy] = useState('Simple RAG')
  const [hybrid, setHybrid] = useState(false) // Graph RAG: list + LLM synthesis
  const [useCache, setUseCache] = useState(true)
  const [viewing, setViewing] = useState(null) // a history entry being viewed
  const [asked, setAsked] = useState('') // question behind the shown answer
  const [thread, setThread] = useState([]) // conversation turns for follow-up asks
  const [scopeProjects, setScopeProjects] = useState([]) // extra projects joined into the ask
  const askScope = useAskScope()
  const [viewer, setViewer] = useState(null) // { source, chunkIndex } for the doc viewer
  const openDoc = (source, chunkIndex, asOf) => source && setViewer({ source, chunkIndex, asOf })

  const historyQuery = useHistory(mode === 'ask' ? project : '')
  const delHistory = useDeleteHistory()
  const clrHistory = useClearHistory()

  // Seed the Ask controls from the saved per-call defaults (once). The user can
  // still override the strategy in the picker below; an explicit choice wins.
  const defaultsQuery = useDefaults()
  const seededDefaults = useRef(false)
  useEffect(() => {
    const d = defaultsQuery.data?.rag
    if (d && !seededDefaults.current) {
      seededDefaults.current = true
      if (d.strategy) setStrategy(d.strategy)
      setUseCache(d.use_cache !== false)
    }
  }, [defaultsQuery.data])

  useEffect(() => {
    if (!project && projectsQuery.data?.current_project) setProject(projectsQuery.data.current_project)
  }, [project, projectsQuery.data])
  useEffect(() => {
    setViewing(null)
    setThread([]) // a thread is a conversation over ONE project's corpus
    setScopeProjects((sp) => sp.filter((p) => p !== project))
  }, [project, mode])

  // A concept clicked in the Map/Topics views seeds an Ask question here.
  useEffect(() => {
    if (seedNonce && seedQuestion) {
      setMode('ask')
      setQuery(seedQuestion)
    }
  }, [seedNonce]) // eslint-disable-line react-hooks/exhaustive-deps

  const projects = projectsQuery.data?.projects ?? []
  const allStrategies = strategiesQuery.data?.strategies ?? []
  const history = historyQuery.data?.history ?? []

  // A project is "code-backed" if it has a repo bound (Graph RAG scope). Only
  // then do we offer Graph RAG / hybrid — otherwise those strategies are noise.
  const codeReposQ = useCodeRepos(project)
  const isCodeProject = (codeReposQ.data?.code_repos?.length ?? 0) > 0
  const strategies = allStrategies.filter((s) => !s.code_only || isCodeProject)
  const selected = allStrategies.find((s) => s.name === strategy)
  const graphSelected = !!selected?.supports_hybrid && isCodeProject

  // If the chosen strategy stops being valid (e.g. Graph RAG selected, then you
  // switch to a project with no bound repo), fall back to Simple RAG.
  useEffect(() => {
    if (strategy !== 'Simple RAG' && allStrategies.length &&
        !strategies.some((s) => s.name === strategy)) {
      setStrategy('Simple RAG')
    }
  }, [strategy, isCodeProject, allStrategies.length]) // eslint-disable-line react-hooks/exhaustive-deps

  const invalidateHistory = () => qc.invalidateQueries({ queryKey: ['rag', 'history', project] })

  const onSubmit = (e) => {
    e.preventDefault()
    const q = query.trim()
    if (!q) return
    setViewing(null)
    if (mode === 'ask') {
      setAsked(q)
      // Scope ask: extra projects joined in → one merged, cited answer
      // (bypasses thread/cache — composed, not stored).
      if (scopeProjects.length > 0) {
        askScope.mutate(
          { question: q, projects: [project, ...scopeProjects] },
          { onSuccess: () => setQuery('') }
        )
        return
      }
      // Threaded Ask: with a live thread, follow-ups carry the prior turns —
      // the BFF condenses them into a standalone question (shown on the answer).
      const payload = { question: q, project, strategy, use_cache: useCache }
      if (graphSelected && hybrid) payload.retrieval_mode = 'hybrid'
      if (thread.length > 0) payload.thread = thread
      ask.mutate(payload, {
        onSuccess: (r) => {
          if (!r) return
          if (!r.cached) invalidateHistory()
          setThread((t) => [...t.slice(-3), {
            question: r.condensed_question ?? q,
            answer: String(r.answer || '').slice(0, 700),
          }])
          setQuery('')
        },
      })
    } else {
      search.mutate({ query: q, project, k: 8 })
    }
  }

  const onFresh = () => {
    if (!asked) return
    setViewing(null)
    ask.mutate({
      question: asked, project, strategy, use_cache: false,
      ...(graphSelected && hybrid ? { retrieval_mode: 'hybrid' } : {}),
    }, { onSuccess: invalidateHistory })
  }

  const viewEntry = (entry) => {
    setAsked(entry.question)
    setViewing(entry)
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  const shown = mode === 'ask'
    ? viewing ?? (scopeProjects.length > 0 ? askScope.data : ask.data)
    : null
  const pending = search.isPending || ask.isPending || askScope.isPending

  // Prefetch referenced documents in the background so opening the viewer is
  // instant (the first get_document per doc pays a one-time Chroma cost).
  useEffect(() => {
    const sources = []
    shown?.sources?.forEach((s) => {
      const v = s.source || s.metadata?.source
      if (v && v !== 'knowledge_graph') sources.push(v) // graph facts aren't docs
    })
    search.data?.results?.forEach((r) => {
      const v = r.source || r.metadata?.source
      if (v && v !== 'knowledge_graph') sources.push(v)
    })
    if (!project || sources.length === 0) return
    ;[...new Set(sources)].slice(0, 8).forEach((source) => {
      qc.prefetchQuery({
        queryKey: ['rag', 'document', project, source],
        queryFn: () => getDocument({ project, source }),
        staleTime: 5 * 60 * 1000,
      })
    })
  }, [shown, search.data, project]) // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="space-y-8">
      {!embedded && (
        <header>
          <div className="text-[11px] uppercase tracking-[0.22em] text-gold-deep dark:text-gold mb-2">Illuminating Truth</div>
          <h1 className="font-display text-4xl text-ink dark:text-mist">Ask your archive</h1>
          <p className="mt-2 text-ink-soft dark:text-mist/60">
            {mode === 'ask'
              ? 'Get a generated answer with cited sources. Repeat questions are served from cache.'
              : 'Semantic search across your indexed documents. Retrieval only — no AI, no cost.'}
          </p>
        </header>
      )}

      <div className="inline-flex rounded-lg border border-parchment-border dark:border-midnight-600 p-0.5">
        {[
          ['search', 'Search', SearchIcon],
          ['ask', 'Ask', MessageSquare],
        ].map(([m, label, Icon]) => (
          <button
            key={m}
            type="button"
            onClick={() => setMode(m)}
            className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm transition ${
              mode === m ? 'bg-gold/15 text-gold-deep dark:text-gold' : 'text-ink-soft dark:text-mist/60'
            }`}
          >
            <Icon className="w-4 h-4" />
            {label}
          </button>
        ))}
      </div>

      <form onSubmit={onSubmit} className="space-y-3">
        <div className="flex flex-col sm:flex-row gap-3">
          <select
            value={project}
            onChange={(e) => setProject(e.target.value)}
            className="h-12 rounded-lg px-3 text-sm bg-white border border-parchment-border text-ink focus:outline-none focus:ring-2 focus:ring-gold/50 dark:bg-midnight-700 dark:border-midnight-600 dark:text-mist"
          >
            {projectsQuery.isLoading && <option>Loading projects…</option>}
            {projects.map((p) => (
              <option key={p.name} value={p.name}>
                {p.name} · {p.chunks_count} chunks
              </option>
            ))}
          </select>

          <div className="relative flex-1">
            <SearchIcon className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-ink-soft dark:text-mist/50" />
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={mode === 'ask' ? 'Ask a question…' : 'Search your archive…'}
              className="w-full h-12 rounded-lg pl-11 pr-4 text-[15px] bg-white border border-gold/60 text-ink placeholder:text-ink-soft/60 focus:outline-none focus:ring-2 focus:ring-spark/40 focus:border-gold dark:bg-midnight-800 dark:border-midnight-600 dark:text-mist dark:placeholder:text-mist/40 dark:focus:shadow-spark"
            />
          </div>

          <Button type="submit" size="lg" loading={pending} disabled={!query.trim()}>
            <Sparkles className="w-4 h-4" />
            {mode === 'ask' ? 'Ask' : 'Reveal'}
          </Button>
        </div>

        {mode === 'ask' && (
          <div className="flex flex-wrap items-center gap-3 text-sm">
            <label className="text-ink-soft dark:text-mist/50">Strategy</label>
            <select
              value={strategy}
              onChange={(e) => setStrategy(e.target.value)}
              className="h-9 rounded-lg px-2 text-sm bg-white border border-parchment-border text-ink focus:outline-none focus:ring-2 focus:ring-gold/50 dark:bg-midnight-700 dark:border-midnight-600 dark:text-mist"
            >
              {(() => {
                const list = strategies.length ? strategies : [{ name: 'Simple RAG', recommended: true }]
                const rec = list.filter((s) => s.recommended)
                const adv = list.filter((s) => !s.recommended)
                const opt = (s) => <option key={s.name} value={s.name}>{s.name}</option>
                // Recommended first (curated), the rest under "Advanced" so the
                // dropdown isn't the raw research-strategy menu.
                return adv.length ? (
                  <>
                    <optgroup label="Recommended">{rec.map(opt)}</optgroup>
                    <optgroup label="Advanced">{adv.map(opt)}</optgroup>
                  </>
                ) : list.map(opt)
              })()}
            </select>
            {/* Only meaningful for Graph RAG on a code project: keep the exact
                structural list but add an LLM explanation. Label deliberately
                avoids the word "hybrid" — it collides with the "Hybrid: *"
                vector presets and users picked the wrong one. */}
            {graphSelected && (
              <label className="inline-flex items-center gap-1.5 text-xs text-ink-soft dark:text-mist/60 cursor-pointer"
                title="Keep the exact structural class list from the graph AND add an LLM explanation over the graph + code context (depth, packages, how they're used)">
                <input type="checkbox" checked={hybrid} onChange={(e) => setHybrid(e.target.checked)}
                  className="accent-gold" />
                + explain
              </label>
            )}
            <span className="text-xs text-gold-deep dark:text-gold">{PROVIDERS_NOTE}</span>
          </div>
        )}

        {projectsQuery.isError && <ErrorNote err={projectsQuery.error} prefix="Couldn't load projects" />}
      </form>

      {/* Scope ask — join extra projects into the question (merged retrieval) */}
      {mode === 'ask' && (
        <div className="flex flex-wrap items-center gap-2 text-xs text-ink-soft dark:text-mist/50">
          {scopeProjects.map((p) => (
            <span key={p} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-cyan-glow/10 text-cyan-800 dark:text-cyan-300">
              + {p}
              <button onClick={() => setScopeProjects((sp) => sp.filter((x) => x !== p))} aria-label={`Remove ${p}`}>×</button>
            </span>
          ))}
          <select
            value=""
            onChange={(e) => e.target.value && setScopeProjects((sp) => [...sp, e.target.value])}
            className="h-7 rounded-md px-1.5 text-xs bg-white border border-parchment-border text-ink-soft dark:bg-midnight-700 dark:border-midnight-600 dark:text-mist/60"
            title="Zeptat se napříč více projekty najednou (sloučený retrieval, jedna odpověď s citacemi)"
          >
            <option value="">+ zeptat se i v projektu…</option>
            {projects.filter((p) => p.name !== project && !scopeProjects.includes(p.name)).map((p) => (
              <option key={p.name} value={p.name}>{p.name}</option>
            ))}
          </select>
          {scopeProjects.length > 0 && (
            <span className="italic">scope: {project} + {scopeProjects.join(' + ')} · bez vlákna a cache</span>
          )}
        </div>
      )}
      {mode === 'ask' && askScope.isError && <ErrorNote err={askScope.error} prefix="Scope ask failed" />}

      {/* Threaded Ask — follow-ups resolve pronouns against the running thread */}
      {mode === 'ask' && thread.length > 0 && (
        <div className="flex items-center gap-2 text-xs text-ink-soft dark:text-mist/50">
          <span className="px-2 py-0.5 rounded-full bg-gold/10 text-gold-deep dark:text-gold">
            ↳ vlákno · {thread.length} {thread.length === 1 ? 'otázka' : 'otázky'}
          </span>
          <span className="truncate max-w-[420px] italic">„{thread[thread.length - 1].question}"</span>
          <button
            onClick={() => setThread([])}
            className="underline hover:text-gold-deep dark:hover:text-gold"
            title="Zapomenout kontext — další otázka začne od čistého stolu"
          >
            nové vlákno
          </button>
        </div>
      )}

      {/* Truth lens v1.5 — weigh a free-standing statement against the corpus */}
      {mode === 'ask' && <ClaimVerifier project={project} />}

      {mode === 'ask' && ask.isError && <ErrorNote err={ask.error} prefix="Ask failed" />}
      {shown && <Answer key={shown.qa_id || shown.id || shown.timestamp} data={shown} onFresh={onFresh} busy={ask.isPending} onView={openDoc} project={project} />}

      {mode === 'search' && search.isError && <ErrorNote err={search.error} prefix="Search failed" />}
      {mode === 'search' && search.data && <SearchResults result={search.data} onView={openDoc} />}

      {mode === 'ask' && history.length > 0 && (
        <History
          history={history}
          onView={viewEntry}
          onDelete={(id) => delHistory.mutate({ project, id })}
          onClear={() => clrHistory.mutate({ project })}
        />
      )}

      {!shown && !search.data && !search.isError && !ask.isError && <Hint mode={mode} />}

      {viewer && (
        <DocumentViewer
          project={project}
          source={viewer.source}
          highlightChunkIndex={viewer.chunkIndex}
          asOf={viewer.asOf}
          onClose={() => setViewer(null)}
        />
      )}
    </div>
  )
}

function Answer({ data, onFresh, busy, onView, project }) {
  const sources = data.sources ?? []
  const cost = data.cost ?? data.cost_metrics
  const costStr = formatCost(cost)

  // Language tabs: the original answer + any translations stored on the Q&A entry,
  // plus a control to translate into a new language (persisted to the entry).
  const qaId = data.qa_id || data.id
  const origLang = data.response_language || null
  const origText = stripTrailingReferences(data.answer)
  const [added, setAdded] = useState({}) // translations added this session (before refetch)
  const translations = { ...(data.translations || {}), ...added }
  const langs = Object.keys(translations)
  const [active, setActive] = useState('__orig__')
  const [target, setTarget] = useState('')
  const translateAnswer = useTranslateAnswer()

  const targets = TARGET_LANGS.filter(([c]) => c !== origLang && !langs.includes(c))
  const effTarget = target || targets[0]?.[0] || 'en'
  const activeText = active === '__orig__' ? origText : translations[active]
  const activeLang = active === '__orig__' ? origLang || 'en' : active
  const tabs = [['__orig__', origLang ? langLabel(origLang) : 'Original'], ...langs.map((l) => [l, langLabel(l)])]

  const doTranslate = () => {
    if (!qaId) return
    translateAnswer.mutate(
      { project, qa_id: qaId, text: origText, target_lang: effTarget },
      { onSuccess: (r) => { if (r.translated) { setAdded((p) => ({ ...p, [r.language]: r.translated })); setActive(r.language) } } }
    )
  }

  // Truth lens: weigh the ORIGINAL answer's claims against the corpus.
  const lens = useTruthLens()
  const [lensOpen, setLensOpen] = useState(false)
  const runLens = () => {
    if (lensOpen) return setLensOpen(false)
    setLensOpen(true)
    if (!lens.data && !lens.isPending) {
      lens.mutate({ project, qa_id: qaId, answer: origText })
    }
  }

  return (
    <section className="space-y-5">
      <article className="rounded-xl border border-gold/40 bg-white/70 px-6 py-5 dark:border-gold/30 dark:bg-midnight-800/60">
        {data.condensed_question && (
          <div className="mb-3 text-xs text-ink-soft dark:text-mist/50" title={`Původní follow-up: „${data.condensed_from}"`}>
            ↳ zeptáno jako: <span className="italic">„{data.condensed_question}"</span>
          </div>
        )}
        {data.cached && (
          <div className="mb-3 flex items-center gap-3 text-xs">
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-spark/10 text-spark-600 dark:text-spark border border-spark/30">
              <Zap className="w-3 h-3" /> cached — no LLM cost
            </span>
            <button
              type="button"
              onClick={onFresh}
              disabled={busy}
              className="inline-flex items-center gap-1 text-gold-deep dark:text-gold hover:underline disabled:opacity-50"
            >
              <RotateCw className="w-3 h-3" /> get a fresh answer
            </button>
          </div>
        )}

        {langs.length > 0 && (
          <div className="mb-3 flex flex-wrap gap-1 border-b border-parchment-border dark:border-midnight-600">
            {tabs.map(([k, label]) => (
              <button
                key={k}
                onClick={() => setActive(k)}
                className={`px-3 py-1.5 text-sm rounded-t-md -mb-px border-b-2 transition ${
                  active === k ? 'border-gold text-gold-deep dark:text-gold' : 'border-transparent text-ink-soft hover:text-ink dark:text-mist/50 dark:hover:text-mist'
                }`}
              >
                {label}{k === '__orig__' && origLang ? ' · original' : ''}
              </button>
            ))}
          </div>
        )}

        <Markdown>{activeText}</Markdown>

        <div className="mt-4 flex flex-wrap items-center gap-x-6 gap-y-3">
          <ListenButton text={activeText} language={ttsLocale(activeLang)} />
          {/* Truth lens + persisted translations need a Q&A entry — scope
              answers are composed, not stored, so they only offer Listen. */}
          {qaId && <TruthLensButton onClick={runLens} active={lensOpen} pending={lens.isPending} />}
          {qaId && targets.length > 0 && (
            <div className="flex items-center gap-2">
              <select
                value={effTarget}
                onChange={(e) => setTarget(e.target.value)}
                aria-label="Translate answer to"
                className="h-8 rounded-lg px-2 text-xs bg-white border border-parchment-border text-ink focus:outline-none focus:ring-2 focus:ring-gold/50 dark:bg-midnight-700 dark:border-midnight-600 dark:text-mist"
              >
                {targets.map(([c, l]) => <option key={c} value={c}>{l}</option>)}
              </select>
              <button
                onClick={doTranslate}
                disabled={translateAnswer.isPending || !qaId}
                className="inline-flex items-center gap-1.5 px-3 h-8 rounded-lg text-xs font-medium border border-gold/50 text-gold-deep hover:bg-gold/10 disabled:opacity-50 dark:text-gold transition"
              >
                {translateAnswer.isPending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Languages className="w-3.5 h-3.5" />}
                {translateAnswer.isPending ? 'Translating…' : 'Translate'}
              </button>
            </div>
          )}
          {translateAnswer.isError && (
            <span className="inline-flex items-center gap-1 text-xs text-red-600"><AlertTriangle className="w-3.5 h-3.5" /> {translateAnswer.error?.message}</span>
          )}
        </div>

        {lensOpen && <TruthLensPanel lens={lens} />}

        <div className="mt-4 pt-3 border-t border-parchment-border dark:border-midnight-600 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-ink-soft dark:text-mist/50">
          <span>strategy: {data.strategy}</span>
          {typeof data.confidence === 'number' && <span>confidence: {(data.confidence * 100).toFixed(0)}%</span>}
          {data.retrieval_confidence?.tier && (
            <span
              className={
                data.retrieval_confidence.tier === 'high'
                  ? 'text-emerald-700 dark:text-emerald-300'
                  : data.retrieval_confidence.tier === 'medium'
                    ? 'text-gold-deep dark:text-gold'
                    : 'text-red-600'
              }
              title={`How close the corpus is to the question (embedding distance; lower = closer).\nbest: ${data.retrieval_confidence.best_distance} · avg top3: ${data.retrieval_confidence.avg_top3_distance}\nLow tier = the corpus may not really cover this question.`}
            >
              retrieval: {data.retrieval_confidence.tier}
            </span>
          )}
          {costStr && <span className="ml-auto font-mono">{data.cached ? '(cached) ' : ''}{costStr}</span>}
        </div>
      </article>

      {sources.length > 0 && (
        <div className="space-y-2.5">
          <h3 className="font-display text-sm uppercase tracking-[0.14em] text-gold-deep dark:text-gold">References</h3>
          <ol className="space-y-2">
            {sources.map((s, i) => {
              const src = s.source || s.metadata?.source || ''
              // Graph facts (source "knowledge_graph") are structural edges from
              // the code graph, not indexed documents — they must NOT open the
              // doc viewer (there's nothing to fetch). Render them distinct and
              // non-clickable, labeled by the entity.
              const isGraph = src === 'knowledge_graph'
              const label = isGraph ? (s.metadata?.entity || 'Code graph fact') : (basename(src) || '—')
              return (
                <li
                  key={i}
                  id={`src-${i + 1}`}
                  onClick={isGraph ? undefined : () => onView?.(src, s.metadata?.chunk_index, data.timestamp)}
                  className={`scroll-mt-24 flex gap-3 rounded-lg border p-3.5 ${
                    isGraph
                      ? 'border-cyan-glow/30 bg-cyan-glow/5 dark:border-cyan-800/40'
                      : 'border-parchment-border bg-white/60 cursor-pointer hover:border-gold dark:border-midnight-600 dark:bg-midnight-800/50'
                  }`}
                  title={isGraph ? 'Structural fact from the code graph (not a document)' : 'View document'}
                >
                  <span className="shrink-0 mt-0.5 inline-flex items-center justify-center w-6 h-6 rounded-full bg-gold/15 text-gold-deep dark:text-gold text-xs font-semibold border border-gold/30">
                    {i + 1}
                  </span>
                  <div className="min-w-0">
                    <div className="text-sm font-medium text-ink dark:text-mist truncate" title={isGraph ? 'code graph' : src}>
                      {isGraph && (
                        <span className="mr-2 px-1.5 py-0.5 rounded bg-cyan-glow/10 text-cyan-800 dark:text-cyan-300 text-[11px] align-middle">
                          graph
                        </span>
                      )}
                      {s.project && !isGraph && (
                        <span className="mr-2 px-1.5 py-0.5 rounded bg-cyan-glow/10 text-cyan-800 dark:text-cyan-300 text-[11px] align-middle">
                          {s.project}
                        </span>
                      )}
                      {label}
                      {!isGraph && (
                        <span className="ml-2">
                          <TimeChip metadata={s.metadata} />
                        </span>
                      )}
                    </div>
                    <p className="mt-1 text-xs text-ink-soft dark:text-mist/60 line-clamp-2">
                      {stripChunkHeader(s.content ?? s.page_content ?? '').slice(0, 240)}
                    </p>
                  </div>
                </li>
              )
            })}
          </ol>
        </div>
      )}
    </section>
  )
}

function History({ history, onView, onDelete, onClear }) {
  return (
    <section className="space-y-2.5">
      <div className="flex items-center justify-between">
        <h3 className="font-display text-sm uppercase tracking-[0.14em] text-gold-deep dark:text-gold flex items-center gap-1.5">
          <HistoryIcon className="w-3.5 h-3.5" /> History ({history.length})
        </h3>
        <button onClick={onClear} className="text-xs text-ink-soft hover:text-red-600 dark:text-mist/50">
          Clear
        </button>
      </div>
      <ul className="space-y-1.5">
        {history.map((e) => (
          <li
            key={e.id}
            onClick={() => onView(e)}
            className="group flex items-start gap-2 rounded-lg border border-parchment-border bg-white/60 p-3 cursor-pointer hover:border-gold dark:border-midnight-600 dark:bg-midnight-800/50"
          >
            <div className="min-w-0 flex-1">
              <div className="text-sm font-medium text-ink dark:text-mist truncate">{e.question}</div>
              <div className="text-xs text-ink-soft dark:text-mist/50 truncate">
                {(e.answer || '').replace(/[#*`>_]/g, '').slice(0, 120)}
              </div>
            </div>
            <span className="text-[10px] text-ink-soft dark:text-mist/40 whitespace-nowrap mt-0.5">{fmtTime(e.timestamp)}</span>
            <button
              onClick={(ev) => {
                ev.stopPropagation()
                onDelete(e.id)
              }}
              aria-label="Delete entry"
              className="opacity-0 group-hover:opacity-100 p-1 rounded text-ink-soft hover:text-red-600 dark:text-mist/50"
            >
              <Trash2 className="w-3.5 h-3.5" />
            </button>
          </li>
        ))}
      </ul>
    </section>
  )
}

function SearchResults({ result, onView }) {
  return (
    <section className="space-y-3">
      <div className="flex items-baseline justify-between">
        <h2 className="font-display text-lg text-ink dark:text-mist">
          {result.count} {result.count === 1 ? 'match' : 'matches'} in{' '}
          <span className="text-gold-deep dark:text-gold">{result.project}</span>
        </h2>
        {result.count > 0 && <span className="text-xs text-ink-soft dark:text-mist/50">{result.score_note}</span>}
      </div>
      {result.count === 0 ? (
        <div className="rounded-xl border border-parchment-border bg-white/60 p-6 text-ink-soft dark:border-midnight-600 dark:bg-midnight-800/50 dark:text-mist/60">
          No matches in <span className="font-medium">{result.project}</span>. Try another query or project.
        </div>
      ) : (
        <ol className="space-y-3">
          {result.results.map((r, i) => (
            <ResultCard key={i} item={r} rank={i + 1} onView={onView} />
          ))}
        </ol>
      )}
    </section>
  )
}

function ResultCard({ item, rank, onView }) {
  const [expanded, setExpanded] = useState(false)
  const text = stripChunkHeader(item.content ?? item.page_content ?? item.text ?? '')
  const source = item.source || item.metadata?.source || '—'
  const score = typeof item.score === 'number' ? item.score.toFixed(4) : item.score
  const long = text.length > 480
  return (
    <li className="rounded-xl border border-parchment-border bg-white/70 p-5 dark:border-midnight-600 dark:bg-midnight-800/60">
      <p className="text-[15px] leading-relaxed text-ink/90 dark:text-mist/90">
        {expanded || !long ? text : text.slice(0, 480) + '…'}
        {long && (
          <button
            onClick={() => setExpanded((v) => !v)}
            className="ml-1 text-sm text-gold-deep dark:text-gold hover:underline"
          >
            {expanded ? 'show less' : 'show more'}
          </button>
        )}
      </p>
      <div className="mt-3 flex items-center gap-3 text-xs text-ink-soft dark:text-mist/50">
        <span className="inline-flex items-center gap-1.5">
          <span className="w-1.5 h-1.5 rounded-full bg-spark" /> #{rank}
        </span>
        <span className="truncate max-w-[45%]" title={source}>
          {source}
        </span>
        <TimeChip metadata={item.metadata} />
        <span className="font-mono">distance {score}</span>
        <button
          onClick={() => onView?.(item.source || item.metadata?.source, item.metadata?.chunk_index)}
          className="ml-auto text-gold-deep dark:text-gold hover:underline"
        >
          View document →
        </button>
      </div>
    </li>
  )
}

function fmtTime(ts) {
  if (!ts) return ''
  try {
    return new Date(ts).toLocaleString(undefined, {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    })
  } catch {
    return ''
  }
}

function formatCost(cost) {
  if (!cost || typeof cost !== 'object') return null
  const usd = cost.total_cost ?? cost.cost_usd ?? cost.cost ?? cost.usd
  const tokens = cost.total_tokens ?? cost.tokens
  const parts = []
  if (typeof usd === 'number') parts.push(`$${usd.toFixed(4)}`)
  if (typeof tokens === 'number') parts.push(`${tokens} tokens`)
  return parts.join(' · ') || null
}

function basename(p) {
  if (!p) return ''
  const parts = String(p).split(/[\\/]/)
  return parts[parts.length - 1] || p
}

// The LLM often appends its own "References/Sources" list at the end of the
// answer; we render a structured References section separately, so drop that
// trailing block. Only strips a heading-only line in the latter part of the
// text (won't touch a "references" mentioned mid-sentence).
function stripTrailingReferences(md) {
  if (!md) return md
  // Containment, not correctness: an answer is supposed to be a string and the
  // engine is what guarantees it. But when one arrived as an Anthropic content
  // block LIST (2026-09-24, Ask across two projects), md.split threw and the
  // ErrorBoundary replaced the whole Research view with "This view failed to
  // render" — one bad field costing every panel on the page. Fail soft, and say
  // so in the console so the rest still renders and the cause stays findable.
  if (typeof md !== 'string') {
    console.warn('[answer] expected a string, got', Array.isArray(md) ? 'an array' : typeof md, md)
    return ''
  }
  const lines = md.split('\n')
  const re =
    /^[\s#>*_\-\u{1F300}-\u{1FAFF}\u{2600}-\u{27BF}\u{2190}-\u{21FF}]*(?:references?|sources?|citations?|cited\s+sources?)\s*:?\s*[*_\s]*$/iu
  for (let i = lines.length - 1; i >= 0; i--) {
    if (re.test(lines[i])) {
      // Everything from this heading to the end is the LLM's own reference list
      // (redundant with the References section we render from sources). Strip it —
      // but only when there's real answer text before it (so a short answer with a
      // long reference list still gets stripped, unlike the old positional guard).
      let before = lines.slice(0, i).join('\n').trimEnd()
      if (before.replace(/[\s\-—*_#>]/g, '').length < 30) break
      // also drop a trailing horizontal rule left above the heading
      before = before.replace(/\n*\s*(?:-{3,}|_{3,}|\*{3,})\s*$/, '').trimEnd()
      return before
    }
  }
  return md
}

function Hint({ mode }) {
  return (
    <div className="rounded-xl border border-dashed border-parchment-border p-8 text-center text-ink-soft dark:border-midnight-600 dark:text-mist/50">
      {mode === 'ask'
        ? 'Pick a project and ask a question to get a generated answer with sources.'
        : 'Pick a project and ask a question to reveal the most relevant passages.'}
    </div>
  )
}

function ErrorNote({ err, prefix }) {
  return (
    <div className="rounded-xl border border-red-300/70 bg-red-50 p-4 dark:border-red-800/60 dark:bg-red-900/20">
      <div className="flex items-center gap-2 text-red-700 dark:text-red-300 text-sm font-medium">
        <AlertTriangle className="w-4 h-4" />
        {prefix}: {err?.message}
      </div>
      {err?.hint && <p className="mt-1 text-xs text-red-600/90 dark:text-red-400/80">{err.hint}</p>}
    </div>
  )
}
